<template>
  <CModal class="min-w-[720px] bg-base rounded-2" show :z-index="1200">
    <div class="p-6">
      <div class="flex justify-between items-center">
        <div class="text-lg font-semibold color-text mb-3">
          {{ t('canvasUI.canvasBuilder.components.aiPrompt.title') }} (PIA)
        </div>
      </div>
      <div class="flex flex-col w-full relative">
        <CInput
          v-model:value="prompt"
          class="w-full rounded-lg min-h-[250px] text-base z-5"
          :disabled="isProcessing"
          :placeholder="t('canvasUI.canvasBuilder.components.aiPrompt.placeholder')"
          :rows="12"
          type="textarea"
          @keydown="handleKeyDown"
        />
        <div
          v-if="isProcessing"
          class="absolute inset-0 flex flex-col items-center justify-center bg-base/80 z-10 rounded-lg"
        >
          <div class="w-[40px] h-[40px] border-4 border-primary4 border-l-primary rounded-full animate-spin mb-4"></div>
          <div class="text-base font-semibold color-primary">
            {{ t('canvasUI.canvasBuilder.components.aiPrompt.processing') }}
          </div>
        </div>
        <div class="mt-6 flex justify-between items-center">
          <div class="relative z-10 p-1 px-2 rounded bg-primary5 color-text3 text-sm shadow-sm">
            {{ isMac ? '⌘' : 'Ctrl' }} + Enter to submit
          </div>
          <div class="flex justify-end gap-2">
            <CButton :disabled="isProcessing" @click="handleClose">
              {{ isProcessing ? `${t('canvasUI.common.processing')}...` : t('canvasUI.common.cancel') }}
            </CButton>
            <CButton
              class="px-4 py-2 font-semibold z-10"
              :disabled="isProcessing || !prompt.trim()"
              :loading="isProcessing"
              primary
              @click="onSubmit"
            >
              <template #icon>
                <CIcon icon="paper-plane" size="16" />
              </template>
              {{ t('canvasUI.common.submit') }}
            </CButton>
          </div>
        </div>
      </div>
    </div>
  </CModal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import CModal from '@lib/components/CModal/CModal.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CInput from '@lib/components/CInput/CInput.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'

const { t } = useI18n()
const prompt = ref('')
const isMac = navigator && navigator?.platform?.toUpperCase().indexOf('MAC') >= 0

const props = defineProps<{
  isProcessing?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'submit', prompt: string): void
}>()

function onSubmit() {
  if (prompt.value.trim() && !props.isProcessing) {
    emit('submit', prompt.value)
  }
}

function handleClose() {
  if (!props.isProcessing) {
    emit('close')
  }
}

function handleKeyDown(event: KeyboardEvent) {
  // Submit on Ctrl+Enter or Cmd+Enter
  if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && !props.isProcessing) {
    event.preventDefault()
    onSubmit()
  }
}
</script>
